import _ from 'lodash';

/**
 * Convertit une heure au format "HHMM" en objet avec heures et minutes
 * @param {string} heureStr - Heure au format "HHMM" (ex: "0800", "2359")
 * @returns {object} - { hour, minute }
 */
const parseHeureHHMM = (heureStr) => {
  if (!heureStr || heureStr === '') {
    return null;
  }
  const hour = parseInt(heureStr.substring(0, 2), 10);
  const minute = parseInt(heureStr.substring(2, 4), 10);
  return { hour, minute };
};

/**
 * Vérifie si un créneau (bloc) est entièrement occupé selon les horaires
 * de la salle
 * @param {object} creneau - Le créneau à analyser
 * @param {object} salle - Les informations de la salle
 * @param {boolean} isHalfDay - true pour vue WEEK (demi-journée), false pour vue MONTH (journée)
 * @returns {boolean} - true si le bloc est plein, false s'il est partiel
 */
const isBlockFull = (creneau, salle, isHalfDay = true) => {
  const { dateDebut: debutCreneau, reservations } = creneau;

  if (_.isEmpty(reservations)) {
    return false;
  }

  // Déterminer les horaires d'ouverture/fermeture de la salle
  const ouverture = parseHeureHHMM(salle?.heureDebut);
  const fermeture = parseHeureHHMM(salle?.heureFin);

  // Définir les limites théoriques du créneau
  let debutTheorique;
  let finTheorique;

  if (isHalfDay) {
    // Vue WEEK : créneaux par demi-journée (matin ou après-midi)
    const isMorning = debutCreneau.hour() < 12;

    if (isMorning) {
      // Créneau du matin : de l'ouverture (ou 00:00) à 12:00
      if (ouverture) {
        debutTheorique = debutCreneau.clone()
          .startOf('day')
          .hour(ouverture.hour)
          .minute(ouverture.minute);
      } else {
        debutTheorique = debutCreneau.clone().startOf('day');
      }
      finTheorique = debutCreneau.clone().startOf('day').hour(12).minute(0);
    } else {
      // Créneau de l'après-midi : de 12:00 à la fermeture (ou 23:59)
      debutTheorique = debutCreneau.clone().startOf('day').hour(12).minute(0);
      if (fermeture) {
        finTheorique = debutCreneau.clone()
          .startOf('day')
          .hour(fermeture.hour)
          .minute(fermeture.minute);
      } else {
        finTheorique = debutCreneau.clone().endOf('day');
      }
    }
  } else {
    // Vue MONTH : créneaux par journée complète
    if (ouverture) {
      debutTheorique = debutCreneau.clone()
        .startOf('day')
        .hour(ouverture.hour)
        .minute(ouverture.minute);
    } else {
      debutTheorique = debutCreneau.clone().startOf('day');
    }

    if (fermeture) {
      finTheorique = debutCreneau.clone()
        .startOf('day')
        .hour(fermeture.hour)
        .minute(fermeture.minute);
    } else {
      finTheorique = debutCreneau.clone().endOf('day');
    }
  }

  // Temps total disponible dans le créneau (en minutes)
  const tempsDisponibleTotal = finTheorique.diff(debutTheorique, 'minute');

  // Créer des intervalles de temps pour gérer les chevauchements
  const intervalles = [];

  _.forEach(reservations, (reservation) => {
    const { dateDebut, dateFin } = reservation;

    // Borner la réservation aux limites du créneau théorique
    const debutEffectif = dateDebut.isBefore(debutTheorique)
      ? debutTheorique
      : dateDebut.isAfter(finTheorique)
        ? finTheorique
        : dateDebut;

    const finEffective = dateFin.isAfter(finTheorique)
      ? finTheorique
      : dateFin.isBefore(debutTheorique)
        ? debutTheorique
        : dateFin;

    if (debutEffectif.isBefore(finEffective)) {
      intervalles.push({
        debut: debutEffectif,
        fin: finEffective,
      });
    }
  });

  // Fusionner les intervalles qui se chevauchent
  const intervallesTries = _.sortBy(intervalles, 'debut');
  const intervallesFusionnes = [];

  _.forEach(intervallesTries, (intervalle) => {
    if (_.isEmpty(intervallesFusionnes)) {
      intervallesFusionnes.push(intervalle);
    } else {
      const dernier = _.last(intervallesFusionnes);
      if (intervalle.debut.isSameOrBefore(dernier.fin)) {
        // Chevauchement : fusionner
        dernier.fin = intervalle.fin.isAfter(dernier.fin)
          ? intervalle.fin
          : dernier.fin;
      } else {
        // Pas de chevauchement : ajouter
        intervallesFusionnes.push(intervalle);
      }
    }
  });

  // Calculer le temps total réservé après fusion
  let tempsReserve = 0;
  _.forEach(intervallesFusionnes, ({ debut, fin }) => {
    tempsReserve += fin.diff(debut, 'minute');
  });

  // Le bloc est plein si le temps réservé >= temps disponible total
  return tempsReserve >= tempsDisponibleTotal;
};

export default isBlockFull;
